// Double mini difficulty from a skill's shape. Mirrors
// backend/services/dmtDifficulty.js (FIG Code of Points 2025-2028, DMT §16.1),
// so the skill form can show the value before saving.
import { startsOffFeet } from './skillPositions';

const ROLL = /\broll\b/i;
const PIKE_BONUS = { 2: 0.4, 3: 0.8, 4: 1.6 };
const STRAIGHT_BONUS = { 2: 0.8, 3: 1.6 };
const round1 = (n) => Math.round(n * 10) / 10;

const totalHalfTwists = (h) => (Array.isArray(h)
  ? h.reduce((s, t) => s + (t || 0), 0)
  : String(h || '').replace(/\D/g, '').split('').reduce((t, d) => t + Number(d), 0));

function singleSomTwistValue(halfTwists) {
  let value = 0;
  for (let i = 1; i <= halfTwists; i++) {
    if (i <= 2) value += 0.2;
    else if (i <= 4) value += 0.3;
    else if (i <= 6) value += 0.4;
    else if (i <= 8) value += 0.5;
    else value += 0.6;
  }
  return value;
}

const fmt = (n) => n.toFixed(1);
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

// Single-somersault twist values come in bands: ½ twists 1-2 are 0.2 each,
// 3-4 are 0.3, 5-6 are 0.4, 7-8 are 0.5 and any more are 0.6.
const TWIST_BANDS = [[1, 2, 0.2], [3, 4, 0.3], [5, 6, 0.4], [7, 8, 0.5], [9, Infinity, 0.6]];

// The DMT difficulty with the working behind it: { value, steps, reason }.
// value is null (with a reason) when the skill has no DMT value. Each step is
// { label, amount } where amount is display text such as "+0.5" or "× 2".
export function explainDmtDifficulty(skill) {
  const none = (reason) => ({ value: null, steps: [], reason });
  if (skill?.prepSkill) return none('No double mini value: prep skills have no tariff.');
  if (!skill || skill.quarterSoms == null) return none('Choose the shape to work it out.');
  if (skill.landing !== 'feet') return none('No double mini value: it must land on the feet.');
  if (skill.quarterSoms % 4 !== 0) return none('No double mini value: only whole somersaults count.');
  if (startsOffFeet(skill)) return none('No double mini value: it must take off from the feet.');
  if (ROLL.test(skill.name || '')) return none('No double mini value for rolls.');

  const soms = skill.quarterSoms / 4;
  const twists = totalHalfTwists(skill.halfTwistsPerSom);
  const { shape } = skill;

  if (soms === 0) {
    if (twists === 0) return { value: 0.1, steps: [{ label: 'Jump with no somersault or twist', amount: '+0.1' }] };
    const value = round1(twists * 0.2);
    return { value, steps: [{ label: `${plural(twists, '½ twist')} with no somersault, 0.2 each`, amount: `+${fmt(value)}` }] };
  }

  if (soms === 1) {
    const steps = [{ label: 'Single somersault', amount: '+0.5' }];
    TWIST_BANDS.forEach(([from, to, each]) => {
      const count = Math.max(0, Math.min(twists, to) - from + 1);
      if (count === 0) return;
      const more = from > 1 ? ' more' : '';
      steps.push({ label: `${count}${more} ½ twist${count === 1 ? '' : 's'} at ${fmt(each)}`, amount: `+${fmt(count * each)}` });
    });
    const bonus = twists === 0 && (shape === 'pike' || shape === 'straight') ? 0.1 : 0;
    if (bonus) steps.push({ label: `${shape === 'pike' ? 'Pike' : 'Straight'} with no twist`, amount: '+0.1' });
    return { value: round1(0.5 + singleSomTwistValue(twists) + bonus), steps };
  }

  // Multiple somersaults: (somersaults × 0.5 + ½ twists × 0.2) × somersaults, plus a shape bonus
  const steps = [{ label: `${soms} somersaults at 0.5`, amount: `+${fmt(soms * 0.5)}` }];
  if (twists > 0) steps.push({ label: `${plural(twists, '½ twist')} at 0.2`, amount: `+${fmt(twists * 0.2)}` });
  steps.push({ label: `Multiplied by the ${soms} somersaults`, amount: `× ${soms}` });
  const base = (soms * 0.5 + twists * 0.2) * soms;
  const bonus = shape === 'pike' ? (PIKE_BONUS[soms] ?? 0) : shape === 'straight' ? (STRAIGHT_BONUS[soms] ?? 0) : 0;
  if (bonus) steps.push({ label: `${shape === 'pike' ? 'Pike' : 'Straight'} bonus`, amount: `+${fmt(bonus)}` });
  return { value: round1(base + bonus), steps };
}

// The DMT difficulty, or null when the skill has no DMT value.
export function computeDmtDifficulty(skill) {
  return explainDmtDifficulty(skill).value;
}
